import DeepLinking from "@common/utils/DeepLinking"
import {
    apiIdle,
    apiRequest,
    apiRequestWithPrerequest,
} from "@data/redux/actions/api"
import { clearPendingDeepLink } from "@data/redux/actions/notifications"
import { authService } from "@data/services/authService"
import { useApiStatus } from "@common/hooks/useApiStatus"
import {
    setIsParentMode,
    setParentModeFamilyId,
} from "@data/redux/actions/parentMode"
import { createStackNavigator } from "@react-navigation/stack"
import axios from "axios"
import * as Linking from "expo-linking"
import React, { useEffect, useRef } from "react"
import { Platform } from "react-native"
import { connect } from "react-redux"
import NotFound from "../../auth/NotFound"
import {
    ChampionBottomSheetModal,
    ChampionModalContext,
} from "../../student/champions/ChampionBottomSheetModal"
import Header from "../header/Header"
import { ImpersonationBannerScreen } from "../../ports/primary/ui/screens/auth/Impersonate/ImpersonationBannerScreen"
import { NavigationRefContext } from "@common/utils/mobileUtils"
import {
    extractPathFromUrl,
    isAuthOnlyPath,
    isParentModeSwitchDiscardedByLive,
    isParentPath,
    needsModeSwitch,
    resolveMenuScreenName,
} from "@common/utils/navigationUtils"
import { hasRole } from "../../data/utility/ability"
import { SummerFunnelProvider } from "../../summer/context/SummerFunnelContext"
import { StageFunnelProvider } from "../../stage/context/StageFunnelContext"
import { InscriptionFunnelProvider } from "../../inscription/context/InscriptionFunnelContext"
import { theme } from "../theme/theme"
import { navigationScreens } from "./navigationScreens"

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * Match a URL path against registered screen names, handling dynamic segments.
 * e.g. "referrer/dashboard/abc123" matches screen "referrer/dashboard/:h"
 */
const matchScreen = (path) => {
    const allScreens = Object.values(navigationScreens).flat()
    for (const screen of allScreens) {
        const name = screen.name
        if (name === path) return { screenName: name, pathParams: {} }
        if (name.includes(":")) {
            const pattern = name
                .split(/(:[^/]+)/g)
                .map((part) =>
                    part.startsWith(":")
                        ? `(?<${part.slice(1)}>[^/]+)`
                        : escapeRegex(part)
                )
                .join("")
            const regex = new RegExp("^" + pattern + "$")
            const match = path.match(regex)
            if (match)
                return { screenName: name, pathParams: match.groups || {} }
        }
    }
    return null
}

const Stack = createStackNavigator()

const RootNavigator = (props) => {
    const { navigationRef } = React.useContext(NavigationRefContext)

    //** ComponentDidMount
    useEffect(() => {
        if (props.token) {
            axios.defaults.headers.common["Authorization"] =
                "Bearer " + props.token
        }
    }, [props.token])

    // Sortir de l'impersonation déconnecte comme une déconnexion ordinaire : retour à l'accueil.
    // Sans ce retour, une page aussi ouverte hors connexion (« Tous mes lives » par son lien) reste à l'écran, en legacy.
    // Un jeton expiré (2 h) fait répondre la sortie en 401, qui déconnecte aussi : même retour.
    const backToHome = () => navigationRef?.current?.reset({ index: 0, routes: [{ name: "menu" }] })
    useApiStatus(authService.exitImpersonation, null, false, backToHome, (error) => {
        if (error?.status === 401) backToHome()
    }, true)

    // Deep link persistence: navigate after login when a pending deep link exists
    useEffect(() => {
        if (!props.token || !props.pendingDeepLink) return

        const result = extractPathFromUrl(props.pendingDeepLink)
        if (!result?.path) {
            props.clearPendingDeepLink()
            return
        }
        const { path, params } = result

        // Logged in + auth-only target (login, register, etc.) — discard the link.
        if (isAuthOnlyPath(path)) {
            props.clearPendingDeepLink()
            return
        }

        // A parent-only target reached mid-live (P-3581) must not flip the
        // mode — there's no live screen registered in the parent stack to
        // fall back to. Decision of 05/10 (Wajih): discard the link now
        // rather than falling through to "mode is correct" below (which
        // would clear + navigate, yanking the student back to the live
        // screen even from an unrelated one — messenger, an activity...).
        // The link concerns the parent, so nothing replays once the live
        // ends either — `isLiveMode` stays a dep only so this check reads
        // its current value when the link first arrives.
        if (isParentModeSwitchDiscardedByLive(path, props.isLiveMode)) {
            props.clearPendingDeepLink()
            return
        }

        // If a mode switch is needed, set the mode and wait for re-render
        // before navigating (the navigator needs to render the correct screens first)
        if (needsModeSwitch(path, props.isParentMode)) {
            const needsParentMode = isParentPath(path)
            // Parent mode only makes sense on a student account (it's a view
            // toggle, not a separate auth surface). A tutor account that
            // somehow receives a parent path — e.g. a forwarded email link —
            // would flip into an empty parent UI; drop the link instead.
            if (
                needsParentMode &&
                (hasRole(props.user, "tutor") ||
                    hasRole(props.user, "tutor-candidate"))
            ) {
                props.clearPendingDeepLink()
                return
            }
            // Wait for currentUser before flipping to parent mode: ParentMainLayout
            // renders blank without a user, and the follow-up navigate would land
            // on parent-home instead of the deep-linked tab. Re-runs when user lands.
            if (needsParentMode && !props.user) return
            if (needsParentMode) {
                // Resolve parentModeFamilyId from the current session's user so
                // ParentMainLayout shows the family the logged-in account belongs
                // to (not an arbitrary family from storedTokens).
                const mappedFamilyId =
                    props.familyState?.userFamilyMap?.[props.user?.id]
                props.setParentModeFamilyId(mappedFamilyId ?? -1)
            }
            props.setIsParentMode(needsParentMode)
            return
        }

        // Mode is correct — clear the pending link and navigate
        props.clearPendingDeepLink()

        if (!navigationRef?.current) return

        const menuScreen = resolveMenuScreenName(path)
        if (menuScreen) {
            navigationRef.current.navigate("menu", {
                screen: menuScreen,
                params,
            })
        } else {
            // Match dynamic-segment screen names (e.g. "referrer/dashboard/:h")
            const matched = matchScreen(path)
            if (matched) {
                navigationRef.current.navigate(matched.screenName, {
                    ...matched.pathParams,
                    ...params,
                })
            } else {
                navigationRef.current.navigate(path, params)
            }
        }
    }, [
        props.token,
        props.pendingDeepLink,
        props.isParentMode,
        props.user,
        props.isLiveMode,
    ])

    const championModalRef = useRef()

    const renderNavigationScreens = (ns, title) =>
        ns.map((screen) => (
            <Stack.Screen
                key={screen.name}
                name={screen.name}
                component={
                    screen.titles?.find((t) =>
                        Object.keys(t)?.includes(title)
                    )?.[title]?.component
                        ? screen.titles?.find((t) =>
                              Object.keys(t).includes(title)
                          )?.[title]?.component
                        : screen?.component
                }
                options={
                    screen.titles?.find((t) =>
                        Object.keys(t).includes(title)
                    )?.[title]?.options
                        ? screen.titles?.find((t) =>
                              Object.keys(t).includes(title)
                          )?.[title]?.options
                        : typeof screen.options === "function"
                          ? screen.options
                          : {
                                headerTitle: screen.name,
                                ...screen.options,
                            }
                }
            />
        ))
    const renderNavigator = () => (
        <Stack.Navigator
            screenOptions={{
                headerMode: "screen",
                header: ({
                    scene,
                    previous,
                    navigation,
                    route,
                    options,
                    back,
                }) => {
                    return (
                        <Header
                            scene={scene}
                            previous={previous}
                            navigation={navigation}
                            route={route}
                            options={options}
                            back={back}
                        />
                    )
                },
                gestureEnabled: false,
                cardStyle: {
                    backgroundColor: theme.colors.whiteAlmost,
                    flex: 1,
                },
            }}
        >
            {props.token ? (
                props.isParentMode ? (
                    <>
                        {/*** Parent Mode screens - only subscription related*/}
                        {renderNavigationScreens(
                            navigationScreens.main.filter((screen) =>
                                screen?.titles?.find((t) =>
                                    Object.keys(t)?.includes("parent")
                                )
                            ),
                            "parent"
                        )}
                    </>
                ) : props.token && props.isLiveMode ? (
                    <>
                        {/*** Live Mode screens*/}
                        {renderNavigationScreens(
                            navigationScreens.main.filter((screen) =>
                                screen?.titles?.find((t) =>
                                    Object.keys(t)?.includes("live")
                                )
                            ),
                            "live"
                        )}
                    </>
                ) : (
                    <>
                        {/*** Main screens*/}
                        {renderNavigationScreens(
                            navigationScreens.main.filter((screen) =>
                                screen?.titles?.find((t) =>
                                    Object.keys(t)?.includes("main")
                                )
                            ),
                            "main"
                        )}
                    </>
                )
            ) : (
                <>
                    {/*** Auth screens*/}
                    {renderNavigationScreens(
                        navigationScreens.main.filter((screen) =>
                            screen?.titles?.find((t) =>
                                Object.keys(t)?.includes("auth")
                            )
                        ),
                        "auth"
                    )}
                </>
            )}
            <Stack.Screen
                name={"not-found"}
                component={NotFound}
                options={{
                    headerTitle: "Page introuvable",
                    headerShown: false,
                }}
            />
        </Stack.Navigator>
    )
    const prefix = Linking.createURL("/")
    // SummerFunnelProvider UNIQUE au-dessus du navigateur : l'état du funnel
    // Cours d'été est partagé en mémoire entre toutes ses étapes (web + natif),
    // au lieu de l'ancien provider par-écran collé par sessionStorage (source
    // des races/clobbers de reprise). Monté ici — et pas dans App.js — pour
    // couvrir les trois piles (auth / main / parent) en un seul point.
    // StageFunnelProvider : même architecture (provider unique) pour le funnel
    // Stage catalogue-driven — état partagé en mémoire entre toutes ses étapes.
    return (
        <SummerFunnelProvider>
            <StageFunnelProvider>
                <InscriptionFunnelProvider>
                <ImpersonationBannerScreen />
                {Platform.OS !== "web" ? (
                    <ChampionModalContext.Provider value={championModalRef}>
                        {renderNavigator()}
                    </ChampionModalContext.Provider>
                ) : (
                    <>
                        <DeepLinking prefix={prefix} />
                        {renderNavigator()}
                    </>
                )}

                {Platform.OS !== "web" && (
                    <ChampionBottomSheetModal ref={championModalRef} />
                )}
                </InscriptionFunnelProvider>
            </StageFunnelProvider>
        </SummerFunnelProvider>
    )
}

const mapStateToProps = (state) => {
    return {
        networkOn: state.api?.networkOn,
        token: state.data.session?.object?.token,
        user: state.data.currentUser?.object,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        isParentMode: state.data.family.isParentMode,
        pendingDeepLink: state.notifications.pendingDeepLink,
        familyState: state.data.family,
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiRequestWithPrerequest,
    apiIdle,
    clearPendingDeepLink,
    setIsParentMode,
    setParentModeFamilyId,
}

export default connect(mapStateToProps, mapDispatchToProps)(RootNavigator)
